import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { useCRM } from '../context/CRMContext';
import { useCRMData } from '../context/CRMDataContext';
import FilterPanel from '../components/common/FilterPanel';
import BaseModal from '../components/modals/BaseModal';
import ContactForm from '../components/forms/ContactForm';
import ImportCSVModal from '../components/modals/ImportCSVModal';
import MassEmailModal from '../components/modals/MassEmailModal';

const Contacts = () => {
  const { integrations, bulkAddContacts } = useCRM();
  const { contacts, companies } = useCRMData();
  const navigate = useNavigate();
  const [activeFilters, setActiveFilters] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isMassEmailModalOpen, setIsMassEmailModalOpen] = useState(false);
  const [isImportingLinkedIn, setIsImportingLinkedIn] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState([]);

  const availableFilters = [
    { key: 'name', label: 'Navn', type: 'text' },
    { key: 'title', label: 'Tittel', type: 'text' },
    { key: 'company', label: 'Bedrift', type: 'text' },
    { key: 'email', label: 'E-post', type: 'text' },
    { key: 'phone', label: 'Telefon', type: 'text' },
    { key: 'role_in_buying', label: 'Rolle', type: 'select', options: [
      { value: 'Beslutter', label: 'Beslutter' },
      { value: 'Påvirker', label: 'Påvirker' },
      { value: 'Bruker', label: 'Bruker' },
      { value: 'Portvakt', label: 'Portvakt' }
    ]}
  ];

  const quickFilters = [
    { label: 'Alle kontakter', isActive: Object.keys(activeFilters).length === 0, onClick: () => setActiveFilters({}) },
    { label: 'Beslutningstakere', isActive: activeFilters.role_in_buying === 'Beslutter', onClick: () => setActiveFilters(prev => prev.role_in_buying === 'Beslutter' ? {} : { role_in_buying: 'Beslutter' }) }
  ];

  const filteredContacts = useMemo(() => {
    return contacts.filter(contact => {
      const email = contact.email || contact.epost || '';
      const phone = contact.phone || contact.mobile || contact.phone_number || '';

      for (const [key, value] of Object.entries(activeFilters)) {
        if (!value) continue;
        const filterValue = value.toLowerCase();
        if (key === 'name' && !contact.name.toLowerCase().includes(filterValue)) return false;
        if (key === 'title' && !contact.title?.toLowerCase().includes(filterValue)) return false;
        if (key === 'email' && !email.toLowerCase().includes(filterValue)) return false;
        if (key === 'phone' && !phone?.includes(filterValue)) return false;
        if (key === 'role_in_buying' && contact.role_in_buying !== value) return false;
        if (key === 'company') {
          const comp = companies.find(c => c.company_id === contact.company_id || c.id === contact.company_id);
          if (!comp || !comp.name.toLowerCase().includes(filterValue)) return false;
        }
      }
      return true;
    });
  }, [contacts, companies, activeFilters]);

  const toggleSelection = (e, contactId) => {
    e.stopPropagation();
    setSelectedContactIds(prev => prev.includes(contactId) ? prev.filter(id => id !== contactId) : [...prev, contactId] );
  };

  const getRoleStyle = (role) => {
    switch (role) {
      case 'Beslutter': return 'bg-rose-50 text-rose-700 border-rose-100';
      case 'Påvirker': return 'bg-sky-50 text-sky-700 border-sky-100';
      case 'Bruker': return 'bg-emerald-50 text-emerald-700 border-emerald-100';
      case 'Portvakt': return 'bg-amber-50 text-amber-700 border-amber-100';
      default: return 'bg-slate-50 text-slate-600 border-slate-100';
    }
  };

  const getCompanyName = (companyId, fallbackName = '') => {
    const company = companies.find(c => c.company_id === companyId || c.id === companyId);
    return company ? company.name : fallbackName || 'Ukjent selskap';
  };

  const handleLinkedInImport = async () => {
    const linkedInIntegration = integrations.find(i => i.name === 'LinkedIn');
    if (!linkedInIntegration?.connected) {
      alert('Du må koble til LinkedIn under "Integrasjoner" for å importere kontakter.');
      navigate('/integrations');
      return;
    }
    setIsImportingLinkedIn(true);
    setTimeout(() => {
      bulkAddContacts([
        { name: 'Jens Stoltenberg', title: 'Generalsekretær', company_id: 'comp-1', email: 'jens@nato.int', role: 'Top Leader', linkedin: 'https://linkedin.com/in/jens', role_in_buying: 'Beslutter' },
        { name: 'Erna Solberg', title: 'Partileder', company_id: 'comp-2', email: 'erna@hoyre.no', role: 'Politiker', linkedin: 'https://linkedin.com/in/erna', role_in_buying: 'Påvirker' }
      ]);
      setIsImportingLinkedIn(false);
      alert('2 kontakter ble importert fra LinkedIn!');
    }, 2000);
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-10 relative">
      <AnimatePresence>
        {selectedContactIds.length > 0 && (
          <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }} className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-slate-900 border border-slate-800 px-8 py-5 rounded-[2.5rem] shadow-2xl flex items-center gap-8 min-w-[400px]">
            <div className="flex items-center gap-4 border-r border-slate-800 pr-8">
              <div className="w-12 h-12 bg-primary-600 rounded-2xl flex items-center justify-center text-white font-black shadow-glow">
                {selectedContactIds.length}
              </div>
              <p className="text-sm font-black text-white uppercase tracking-widest">Kontakter valgt</p>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={() => setSelectedContactIds([])} className="text-xs font-black text-slate-500 uppercase tracking-widest hover:text-white transition-colors">
                Avbryt
              </button>
              <button onClick={() => setIsMassEmailModalOpen(true)} className="flex items-center gap-3 px-8 py-4 bg-primary-600 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] hover:bg-primary-500 transition-all shadow-lg shadow-primary-500/20">
                <SafeIcon icon="📧" className="w-5 h-5" /> Send Masse-epost
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-slate-200">
        <div className="space-y-2">
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">Kontakter</h1>
          <p className="text-slate-500 font-medium text-lg">Administrer dine profesjonelle relasjoner og nettverk.</p>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={handleLinkedInImport} disabled={isImportingLinkedIn} className="flex items-center gap-2 px-6 py-4 bg-blue-50 text-blue-700 border border-blue-100 rounded-[1.25rem] font-bold text-xs uppercase tracking-widest hover:bg-blue-100 transition-all shadow-sm">
            {isImportingLinkedIn ? (
              <>
                <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }}>
                  <SafeIcon icon="⚡" className="w-4 h-4" />
                </motion.div>
                Henter...
              </>
            ) : (
              <>
                <SafeIcon icon="🔗" className="w-4 h-4" /> Importer fra LinkedIn
              </>
            )}
          </button>
          <button onClick={() => setIsImportModalOpen(true)} className="flex items-center gap-2 px-6 py-4 bg-white border border-slate-200 text-slate-600 rounded-[1.25rem] font-bold text-xs uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm">
            <SafeIcon icon="📂" className="w-4 h-4" /> Importer CSV
          </button>
          <button onClick={() => setIsModalOpen(true)} className="flex items-center gap-2 px-8 py-4 bg-slate-900 text-white rounded-[1.25rem] font-black text-xs uppercase tracking-[0.2em] hover:bg-slate-800 transition-all shadow-xl shadow-slate-200">
            <SafeIcon icon="➕" className="w-5 h-5" /> Ny Kontakt
          </button>
        </div>
      </div>

      <FilterPanel availableFilters={availableFilters} activeFilters={activeFilters} onFilterChange={setActiveFilters} quickFilters={quickFilters} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredContacts.map((contact, idx) => {
          const isSelected = selectedContactIds.includes(contact.contact_id);
          const displayPhone = contact.phone || contact.mobile || contact.phone_number || '';

          return (
            <motion.div
              key={contact.contact_id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.05 }}
              onClick={() => navigate(`/contacts/${contact.contact_id}`)}
              className={`group bg-white rounded-[2.5rem] border-2 p-8 hover:border-primary-300 shadow-sm hover:shadow-2xl hover:shadow-primary-500/10 transition-all duration-500 cursor-pointer relative overflow-hidden ${isSelected ? 'border-primary-500 bg-primary-50/30' : 'border-slate-200'}`}
            >
              <div onClick={(e) => toggleSelection(e, contact.contact_id)} className={`absolute top-6 left-6 w-8 h-8 rounded-xl border-2 flex items-center justify-center transition-all z-20 ${isSelected ? 'bg-primary-600 border-primary-600 shadow-glow' : 'bg-white border-slate-200 opacity-0 group-hover:opacity-100'}`}>
                {isSelected && <SafeIcon icon="✓" className="w-5 h-5 text-white" />}
              </div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-slate-50 rounded-bl-[5rem] -mr-16 -mt-16 group-hover:bg-primary-50 transition-colors duration-500" />
              
              <div className="relative flex items-center gap-5 mb-8">
                <div className="ml-10 w-16 h-16 bg-primary-600 rounded-2xl flex items-center justify-center text-white text-xl font-black shadow-lg shadow-primary-200 group-hover:scale-110 transition-transform duration-500">
                  {contact.name.substring(0, 2).toUpperCase()}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-black text-slate-900 leading-tight group-hover:text-primary-600 transition-colors line-clamp-1">{contact.name}</h3>
                  <p className="text-sm font-bold text-slate-400 mt-1">{contact.title}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <div className={`px-3 py-1 rounded-lg border text-[10px] font-black uppercase tracking-widest ${getRoleStyle(contact.role_in_buying)}`}>
                  {contact.role_in_buying || 'Ingen rolle'}
                </div>
              </div>

              <div className="space-y-4 pt-6 border-t border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400">
                    <SafeIcon icon="🏢" className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Selskap</p>
                    <p className="text-sm font-bold text-slate-700">{getCompanyName(contact.company_id, contact.company_name)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400">
                    <SafeIcon icon="📧" className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">E-post</p>
                    <p className="text-sm font-bold text-slate-700 truncate max-w-[180px]">{contact.email}</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex items-center justify-between">
                <div className="flex gap-2">
                  {contact.linkedin && (
                    <a href={contact.linkedin} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400 hover:bg-primary-50 hover:text-primary-600 transition-all">
                      <SafeIcon icon="🔗" className="w-4 h-4" />
                    </a>
                  )}
                  {displayPhone && (
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                      <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400" title={displayPhone}>
                        <SafeIcon icon="📞" className="w-4 h-4" />
                      </div>
                      <span>{displayPhone}</span>
                    </div>
                  )}
                </div>
                <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center text-white translate-x-12 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-500 shadow-lg shadow-slate-200">
                  <SafeIcon icon="➡️" className="w-5 h-5" />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <BaseModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Opprett Kontakt">
        <ContactForm onClose={() => setIsModalOpen(false)} />
      </BaseModal>
      <BaseModal isOpen={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} title="Importer kontakter fra CSV">
        <ImportCSVModal onClose={() => setIsImportModalOpen(false)} />
      </BaseModal>
      <BaseModal isOpen={isMassEmailModalOpen} onClose={() => setIsMassEmailModalOpen(false)} title="Send Masse-epost">
        <MassEmailModal selectedContacts={contacts.filter(c => selectedContactIds.includes(c.contact_id))} onClose={() => { setIsMassEmailModalOpen(false); setSelectedContactIds([]); }} />
      </BaseModal>
    </div>
  );
};

export default Contacts;